/**
 * excursionStore — Supabase persistence for connector excursions
 * (migration 586). System of record: the connector_excursions table,
 * workspace-scoped via RLS. No localStorage (§2.1/§2.2).
 */

import { getSupabaseClient } from '../supabaseClient';
import type { SemanticSnapshot } from './snapshot';

export type ExcursionStatus = 'open' | 'returned' | 'reconciled' | 'abandoned';

export interface FidelityManifestEntry {
  plane: 'content' | 'structure' | 'relationships' | 'semantics';
  note: string;
}

export interface ConnectorExcursion {
  id: string;
  workspaceId: string;
  connectorType: string;
  externalContainerId: string;
  externalContainerUrl?: string;
  scope: { collectionId?: string; itemIds: string[]; scopeFrameId?: string };
  baseline: SemanticSnapshot;
  fidelityManifest: FidelityManifestEntry[];
  status: ExcursionStatus;
  exportedAt?: string;
  returnedAt?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

function rowToExcursion(row: Record<string, unknown>): ConnectorExcursion {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    connectorType: row.connector_type as string,
    externalContainerId: row.external_container_id as string,
    externalContainerUrl: (row.external_container_url as string) ?? undefined,
    scope: (row.scope as ConnectorExcursion['scope']) ?? { itemIds: [] },
    baseline: row.baseline as SemanticSnapshot,
    fidelityManifest: (row.fidelity_manifest as FidelityManifestEntry[]) ?? [],
    status: row.status as ExcursionStatus,
    exportedAt: (row.exported_at as string) ?? undefined,
    returnedAt: (row.returned_at as string) ?? undefined,
    createdBy: (row.created_by as string) ?? undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export interface CreateExcursionInput {
  workspaceId: string;
  connectorType: string;
  externalContainerId: string;
  externalContainerUrl?: string;
  scope: { collectionId?: string; itemIds: string[]; scopeFrameId?: string };
  baseline: SemanticSnapshot;
  fidelityManifest: FidelityManifestEntry[];
  createdBy?: string;
}

export async function createExcursion(
  input: CreateExcursionInput,
): Promise<{ data: ConnectorExcursion | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('connector_excursions')
      .insert({
        workspace_id: input.workspaceId,
        connector_type: input.connectorType,
        external_container_id: input.externalContainerId,
        external_container_url: input.externalContainerUrl ?? null,
        scope: input.scope,
        baseline: input.baseline,
        fidelity_manifest: input.fidelityManifest,
        status: 'open',
        exported_at: new Date().toISOString(),
        created_by: input.createdBy ?? null,
      })
      .select()
      .single();
    if (error) return { data: null, error: error.message };
    return { data: rowToExcursion(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function listExcursions(
  workspaceId: string,
): Promise<{ data: ConnectorExcursion[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('connector_excursions')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false })
      .limit(100);
    if (error) return { data: [], error: error.message };
    return { data: (data as Record<string, unknown>[]).map(rowToExcursion), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getExcursion(
  excursionId: string,
): Promise<{ data: ConnectorExcursion | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('connector_excursions')
      .select('*')
      .eq('id', excursionId)
      .single();
    if (error) return { data: null, error: error.message };
    return { data: rowToExcursion(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/** Status transitions. 'returned' and 'reconciled' stamp returned_at. */
export async function setExcursionStatus(
  excursionId: string,
  status: ExcursionStatus,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const patch: Record<string, unknown> = { status };
    if (status === 'returned' || status === 'reconciled') {
      patch.returned_at = new Date().toISOString();
    }
    const { error } = await supabase
      .from('connector_excursions')
      .update(patch)
      .eq('id', excursionId);
    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
